/**
 * Health Score Trends Component
 * 
 * Shows mini sparkline charts and trend indicators for health scores
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material'
import { LineChart, Line, ResponsiveContainer, YAxis } from 'recharts'
import { healthScoreApi, TrendData, HealthScoreHistoryEntry } from '../../services/healthScoreApi'

interface HealthScoreTrendsProps {
  fpCode: string
  domain?: 'overall' | 'product' | 'sale' | 'system'
  period?: 'week' | 'month' | 'quarter'
  showSparkline?: boolean
  showChangeValue?: boolean
  size?: 'small' | 'medium' | 'large'
}

interface ChartDataPoint {
  date: string
  score: number | null
}

const getTrendIcon = (direction: string) => {
  switch (direction) {
    case 'improving':
      return <TrendingDownIcon sx={{ fontSize: 16, color: '#4caf50' }} />
    case 'declining':
      return <TrendingUpIcon sx={{ fontSize: 16, color: '#f44336' }} />
    case 'stable':
    default:
      return <TrendingFlatIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
  }
}

const getTrendColor = (direction: string) => {
  switch (direction) {
    case 'improving':
      return '#4caf50'
    case 'declining':
      return '#f44336'
    case 'stable':
    default:
      return '#9e9e9e'
  }
}

const formatChangeValue = (change: number) => {
  const sign = change >= 0 ? '+' : ''
  return `${sign}${change.toFixed(1)}`
}

export const HealthScoreTrends: React.FC<HealthScoreTrendsProps> = ({
  fpCode,
  domain = 'overall',
  period = 'week',
  showSparkline = true,
  showChangeValue = false,
  size = 'small'
}) => {
  const [trendData, setTrendData] = useState<TrendData | null>(null)
  const [chartData, setChartData] = useState<ChartDataPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTrendData = async () => {
      setLoading(true)
      setError(null)
      
      try {
        // Get trend analysis
        const trends = await healthScoreApi.getTrends(fpCode, period)
        const domainTrend = trends.trends[domain]
        
        if (domainTrend) {
          setTrendData(domainTrend)
        }

        // If sparkline is requested, get history data for chart
        if (showSparkline) {
          const periodDays = period === 'week' ? 7 : period === 'month' ? 30 : 90
          const history = await healthScoreApi.getHistory(fpCode, periodDays, domain)
          
          const domainHistory = history.history[domain] || []
          
          // Convert to chart format and sort by date
          const chartPoints: ChartDataPoint[] = domainHistory
            .map(entry => ({
              date: entry.date,
              score: entry.score
            }))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .slice(-20) // Last 20 points for performance
          
          setChartData(chartPoints)
        }
      } catch (err: any) {
        console.error('Error fetching trend data:', err)
        setError(err.response?.data?.detail || 'Failed to fetch trend data')
      } finally {
        setLoading(false)
      }
    }

    fetchTrendData()
  }, [fpCode, domain, period, showSparkline])

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
        {size !== 'small' && (
          <Typography variant="caption" color="text.secondary">
            Loading trends...
          </Typography>
        )}
      </Box>
    )
  }

  if (error) {
    return (
      <Tooltip title={error} arrow>
        <Box display="flex" alignItems="center" gap={1}>
          <TrendingFlatIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
          {size !== 'small' && (
            <Typography variant="caption" color="text.secondary">
              No data
            </Typography>
          )}
        </Box>
      </Tooltip>
    )
  }

  if (!trendData) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <TrendingFlatIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
        {size !== 'small' && (
          <Typography variant="caption" color="text.secondary">
            No trend data
          </Typography>
        )}
      </Box>
    )
  }

  const getTooltipContent = () => {
    const periodText = period === 'week' ? '7 days' : period === 'month' ? '30 days' : '90 days'
    return (
      <div>
        <div><strong>{domain} Health Trend</strong></div>
        <div>Direction: {trendData.direction}</div>
        <div>Change: {formatChangeValue(trendData.change)} over {periodText}</div>
        <div>Latest: {trendData.latest_score?.toFixed(1)}</div>
        <div>Previous: {trendData.earliest_score?.toFixed(1)}</div>
      </div>
    )
  }

  const sparklineWidth = size === 'small' ? 40 : size === 'medium' ? 60 : 80
  const sparklineHeight = size === 'small' ? 20 : size === 'medium' ? 30 : 40

  return (
    <Tooltip title={getTooltipContent()} arrow>
      <Box display="flex" alignItems="center" gap={1}>
        {/* Trend Icon */}
        {getTrendIcon(trendData.direction)}
        
        {/* Change Value (if requested) */}
        {showChangeValue && size !== 'small' && (
          <Chip
            label={formatChangeValue(trendData.change)}
            size="small"
            sx={{
              backgroundColor: getTrendColor(trendData.direction),
              color: 'white',
              fontSize: '10px',
              height: '18px',
              '& .MuiChip-label': {
                px: 0.5
              }
            }}
          />
        )}
        
        {/* Sparkline Chart */}
        {showSparkline && chartData.length > 1 && (
          <Box
            sx={{
              width: sparklineWidth,
              height: sparklineHeight,
              '& .recharts-surface': {
                overflow: 'visible'
              }
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <YAxis hide domain={['dataMin', 'dataMax']} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={getTrendColor(trendData.direction)}
                  strokeWidth={1.5}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        
        {/* Trend Description for larger sizes */}
        {size === 'large' && (
          <Typography variant="caption" color="text.secondary">
            {trendData.direction} ({formatChangeValue(trendData.change)})
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

export default HealthScoreTrends